class StickyAtc extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.targetSelector = this.dataset.targetSelector || '.buy-now__button';

    this.priceSelector = this.dataset.priceSelector;
    this.formSelector = this.dataset.formSelector;

    this.container = this;
    this.button = this.querySelector('[data-sticky-atc-button]');
    this.pricesContainers = Array.from(this.querySelectorAll('[data-sticky-atc-prices]'));
    this.arrow = this.querySelector('.sticky-atc__arrow');

    this.onClick = this.onClick.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
    this.onPriceMutate = this.onPriceMutate.bind(this);
    this.onButtonMutate = this.onButtonMutate.bind(this);
    this.onScroll = this.onScroll.bind(this);
  }

  connectedCallback() {
    this.targetEl = document.querySelector(this.targetSelector);
    this.formEl = this.formSelector ? document.querySelector(this.formSelector) : null;
    this.addButtonEl = this.formEl ? this.formEl.querySelector('[name="add"]') : null;

    this.priceEl = this.priceSelector ? document.querySelector(this.priceSelector) : null;

    if (this.button) this.button.addEventListener('click', this.onClick);

    this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
      root: null,
      threshold: 0.05,
    });

    if (this.targetEl) this.intersectionObserver.observe(this.targetEl);

    if (this.priceEl && this.pricesContainers.length) {
      this.syncPrices();
      this.priceObserver = new MutationObserver(this.onPriceMutate);
      this.priceObserver.observe(this.priceEl, { childList: true, subtree: true, characterData: true });
    }

    if (this.addButtonEl) {
      this.syncButtonState();
      this.buttonObserver = new MutationObserver(this.onButtonMutate);
      this.buttonObserver.observe(this.addButtonEl, { attributes: true, attributeFilter: ['disabled', 'style', 'class'] });
    }

    window.addEventListener('resize', () => this.recheck());
    window.addEventListener('scroll', this.onScroll, { passive: true });
    setTimeout(() => this.recheck(), 50);

    this.updateArrowVisibility();
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    if (this.priceObserver) this.priceObserver.disconnect();
    if (this.buttonObserver) this.buttonObserver.disconnect();
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll() {
    this.updateArrowVisibility();
  }

  updateArrowVisibility() {
    if (!this.arrow) return;
    const threshold = window.innerHeight * 1.5;
    const enabled = window.scrollY > threshold;
    this.arrow.classList.toggle('sticky-atc__arrow--enabled', enabled);
  }

  onIntersection(entries) {
    const entry = entries && entries[0];
    if (!entry) return;

    const targetVisible = entry.isIntersecting;
    this.setVisible(!targetVisible);
  }

  recheck() {
    if (!this.targetEl) return;
    const rect = this.targetEl.getBoundingClientRect();
    const inViewport = rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
    this.setVisible(!inViewport);
  }

  setVisible(visible) {
    if (visible) this.container.classList.add('is-visible');
    else this.container.classList.remove('is-visible');

    this.syncButtonState();
  }

  onClick(e) {
    e.preventDefault();

    if (!this.addButtonEl) {
      this.formEl = this.formSelector ? document.querySelector(this.formSelector) : null;
      this.addButtonEl = this.formEl ? this.formEl.querySelector('[name="add"]') : null;
    }

    if (this.addButtonEl) {
      this.addButtonEl.click();
      return;
    }

    if (this.formEl) {
      this.formEl.requestSubmit?.();
    }
  }

  onPriceMutate() {
    this.syncPrices();
  }

  syncPrices() {
    if (!this.priceEl || !this.pricesContainers.length) return;

    const compare = this.priceEl.querySelector('.price__sale s.price-item--regular, .price__sale s .price-item--regular');
    const sale = this.priceEl.querySelector('.price__sale .price-item--sale');
    const regular = this.priceEl.querySelector('.price__regular .price-item--regular');
    const installments = this.priceEl.querySelector('.installments-info');

    const compareText = compare ? compare.textContent.trim() : '';
    const saleText = sale ? sale.textContent.trim() : '';
    const regularText = regular ? regular.textContent.trim() : '';

    const isOnSale = !!saleText && !!compareText;

    const priceText = (isOnSale ? saleText : regularText) || '';

    const installmentsText = installments ? installments.textContent.trim() : '';

    this.pricesContainers.forEach((container) => {
      const compareEl = container.querySelector('[data-sticky-atc-compare]');
      const priceEl = container.querySelector('[data-sticky-atc-price]');
      const installmentsEl = container.querySelector('[data-sticky-atc-installments]');

      if (compareEl) {
        if (isOnSale && compareText) {
          compareEl.textContent = compareText;
          compareEl.style.display = '';
        } else if (compareText) {
          compareEl.textContent = compareText;
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      if (priceEl) priceEl.textContent = priceText;

      if (installmentsEl) {
        installmentsEl.textContent = installmentsText;
        installmentsEl.style.display = installmentsText ? '' : 'none';
      }
    });
  }

  onButtonMutate() {
    this.syncButtonState();
  }

  syncButtonState() {
    if (!this.button || !this.addButtonEl) return;

    const computed = window.getComputedStyle(this.addButtonEl);
    const isDisplayed = computed.display !== 'none' && computed.visibility !== 'hidden';

    if (!isDisplayed) {
      this.container.classList.remove('is-visible');
      return;
    }

    if (this.addButtonEl.hasAttribute('disabled')) {
      this.button.setAttribute('disabled', 'disabled');
      this.button.setAttribute('aria-disabled', 'true');
    } else {
      this.button.removeAttribute('disabled');
      this.button.removeAttribute('aria-disabled');
    }
  }
}

customElements.define('sticky-atc', StickyAtc);
